//@ts-nocheck
//@ts-ignore
/* eslint-disable */
import './App.scss';
import Analytics from '@web-solutions/module-analytics';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { Provider } from './providers/Provider';
import { useEffect, useLayoutEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useStore } from './store/store';
import ReactPixel from 'react-facebook-pixel';
import { reInit as reInitRemoteConfig } from '@web-solutions/core/store/remote-config/actions';
import { useDispatch } from 'react-redux';
import { load } from 'src/store/app/actions';
import { components, layouts, backupFlow } from './consts';
import { debounce } from 'lodash';
import { getEarlyAchievableOffset } from './helpers/getEarlyAchievableOffset';
import { useNav } from './hooks/nav';
import { t } from '@web-solutions/module-localization';
import { Text } from './components/typography/Text/Text';
import { useProgressiveImageLoad } from './hooks/useProgressiveImageLoad';
import DynamicPage from './pages/dynamic-page';
import DynamicMultiPage from './pages/dynamic-page-multi';
import DynamicTransition from './pages/dynamic-transition';
import LtsLayout from './layouts/lts';
import TransitionLayoutNew from './layouts/transition';
import DynamicTransitionImage from './pages/dynamic-transition-with-image';
import DynamicTransitionImageTop from './pages/dynamic-transition-image-top';
import DynamicAnalyzingPage from './pages/dynamic-analyzing';
import DynamicPromisePage from './pages/dynamic-promise';
import DynamicProofPage from './pages/dynamic-proof';
import DynamicPersonalPlan from './pages/dynamic-personal-plan';
import { LTS_PAGES } from './constants/ltsPages';
import { generateLayout } from './layouts/layout-generator';
import { PaymentProcessorProvider } from 'core/payment/payment-processor-provider/paymentProcessorContext';

const parseDynamicPagesConfig = (dynamicPagesParam: any) => {
  try {
    if (!dynamicPagesParam) return [];

    if (Array.isArray(dynamicPagesParam)) {
      return dynamicPagesParam;
    }

    if (typeof dynamicPagesParam === 'string') {
      return JSON.parse(dynamicPagesParam);
    }

    return [];
  } catch (error) {
    return [];
  }
};

function App() {
  const { routeGroups: flow, fbPixelId, dynamicPages } = useRemoteConfig();

  const currentPage = useStore((state) => state.currentPage);
  const setPage = useStore((state) => state.setPage);
  const when = useStore((state) => state.when);
  const skippedPages = useStore((state) => state.skippedPages);
  const addSkippedPage = useStore((state) => state.addSkippedPage);
  const removeSkippedPage = useStore((state) => state.removeSkippedPage);
  const dispatch = useDispatch();

  const { onNext, onPrev } = useNav();
  const initializedRef = useRef(false);
  const [isRemoteConfigLoaded, setIsRemoteConfigLoaded] = useState(false);

  const p = new URLSearchParams(window.location.search);
  const flowUrl = p.get('flow');
  const isChangePaymentMethod = flowUrl === 'change-payment-method';

  const dynamicPagesConfig = useMemo(() => {
    if (!dynamicPages) return [];

    const parsedConfig = parseDynamicPagesConfig(dynamicPages);

    return parsedConfig
      .filter((page) => page && (page.id || page['page-name']))
      .map((page, index) => ({
        id: page.id,
        title: page.title,
        subTitle: page.subTitle,
        options: Array.isArray(page.options)
          ? page.options.map((opt, optIndex) => ({
              emoji: opt.emoji || '',
              text: opt.text,
              value: opt.value,
            }))
          : [],
        choice: page.choice || 'single',
        stats: Array.isArray(page.stats)
          ? page.stats.map((opt, optIndex) => ({
              value: opt.value,
              label: opt.label,
            }))
          : [],
        feedbackTitle: page.feedbackTitle,
        feedbackBody: page.feedbackBody,
        buttonTitle: page.buttonTitle,
        sections: Array.isArray(page.sections) ? page.sections : [],
        type: page.type,
      }));
  }, [dynamicPages]);

  useEffect(() => {
    if (flow !== undefined) {
      setIsRemoteConfigLoaded(true);
    }
  }, [flow]);

  const getMergedFlow = useCallback(() => {
    if (!isRemoteConfigLoaded) {
      return backupFlow.flat();
    }

    const baseFlow = flow?.length > 0 ? flow : backupFlow;
    const flatFlow = baseFlow.flat();

    return flatFlow;
  }, [flow, isRemoteConfigLoaded, dynamicPagesConfig]);

  const quizFlow = useMemo(() => {
    return getMergedFlow();
  }, [getMergedFlow]);

  const fullPageFlow = useMemo(() => {
    return [...quizFlow, 'terms-of-use', 'privacy-policy'];
  }, [quizFlow]);

  useLayoutEffect(() => {
    if (!isRemoteConfigLoaded || currentPage) {
      return;
    }

    const firstPage = quizFlow[0];
    setPage(firstPage);
  }, [isRemoteConfigLoaded, flow, quizFlow, currentPage, setPage]);

  const init = async () => {
      await dispatch(load());
    await dispatch(reInitRemoteConfig());
  };

  useLayoutEffect(() => {
    init();
  }, []);

  const pixelId = useMemo(() => {
    return fbPixelId || '1035517762982907';
  }, [fbPixelId]);

  useEffect(() => {
    if (!pixelId || initializedRef.current || typeof window === 'undefined') return;

    if (!window.fbq) {
      (function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = true;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = true;
        t.src = v;
        s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s);
      })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }

    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    window.fbq.initialized = true;

    if (ReactPixel) {
      ReactPixel.init(pixelId, { autoConfig: true, debug: true });
    }

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>`;
    document.body.appendChild(noscript);

    initializedRef.current = true;
  }, [pixelId]);

  useEffect(() => {
    if (!currentPage || !quizFlow.length || !isRemoteConfigLoaded) return;

    if (currentPage === quizFlow[0]) {
      Analytics.trackEvent('first_screen', 'loaded', {
        time: Math.round(performance.now() / 1000),
      });
    }

    const stepIndex = quizFlow.findIndex((page) => page === currentPage) + 1;
    if (stepIndex > 0) {
      Analytics.trackEvent(`step${stepIndex}`, 'view', {
        page: currentPage,
      });
    }

    if (currentPage === quizFlow[1]) {
      gtag('event', 'conversion', {
        send_to: 'AW-17149973324/Y5vFCJKtyP0aEMym3_E_',
        value: 0.1,
        currency: 'USD',
      });
      if (typeof ReactPixel !== 'undefined') {
        ReactPixel.trackCustom('step1_completed', { nexPage: currentPage });
      } else if (window.fbq) {
        fbq('trackCustom', 'step1_completed', { nexPage: currentPage });
      }
    }
  }, [currentPage, quizFlow, isRemoteConfigLoaded]);

  useEffect(() => {
    if (currentPage !== 'goal-2') return;

    const offset = getEarlyAchievableOffset(when);
    const goal2Skipped = skippedPages.includes('goal-2');

    if (offset === null && !goal2Skipped) {
      addSkippedPage('goal-2');
      onNext();
    }
  }, [when, currentPage, skippedPages, onNext]);

  useEffect(() => {
    const offset = getEarlyAchievableOffset(when);
    const shouldShowGoal2 = offset !== null;
    const goal2Skipped = skippedPages.includes('goal-2');

    if (shouldShowGoal2 && goal2Skipped) {
      removeSkippedPage('goal-2');
    }
  }, [when, skippedPages]);

  const debouncedTrackEvent = useCallback(
    debounce((flow) => {
      Analytics.trackEvent(`quizFlow`, 'empty', { flow });
    }, 2000),
    []
  );

  useEffect(() => {
    if (flow?.length === 0 || flow === undefined) {
      debouncedTrackEvent(flow);
    }

    return () => {
      debouncedTrackEvent.cancel();
    };
  }, [flow, debouncedTrackEvent]);

  window.scrollTo(0, 0);

  useProgressiveImageLoad(currentPage, quizFlow);

  if (!isRemoteConfigLoaded) {
    return (
      <div className='page-loader'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='74px'
          height='74px'
          viewBox='0 0 100 100'
          preserveAspectRatio='xMidYMid'
        >
          <circle
            cx='50'
            cy='50'
            fill='none'
            stroke='#007CFF'
            strokeWidth='10'
            r='35'
            strokeLinecap='round'
            strokeDasharray='164.93361431346415 56.97787143782138'
          ></circle>
        </svg>

        <Text semibold>{t('onboarding.preLoader.title')}</Text>
        <Text className='description' size='m' medium>
          {t('onboarding.preLoader.subTitle')}
        </Text>
      </div>
    );
  }

  const layout = generateLayout(fullPageFlow, dynamicPagesConfig, onNext, onPrev);
  const currentLayout = layout[currentPage];

  return (
    <div className='App'>
      <Provider>{currentLayout}</Provider>
    </div>
  );
}

export default App;
